/**
 * Call presentation — one shaping layer shared by every portal.
 *
 * The One Up admin, Cascade, and Client portals previously each built their own
 * call view, so a fix to one never reached the others. This module owns the
 * *decisions* about what a reviewer sees and in what order; the React components
 * in @command-center/oneup-portal-ui own how it looks. Keeping the decisions here
 * means they are pure, unit-testable, and identical in all three surfaces.
 *
 * Audience is a first-class input: `includeAdmin` gates cost and provider
 * internals so a client never sees them, and it is applied here rather than in
 * each portal's template, where one missed conditional leaks pricing.
 */

import type { StructuredOutputRecord } from './structured-outputs.js';
import {
  deriveCallerIdentity,
  slugify,
  structuredOutputSources,
} from './structured-outputs.js';

export interface CallPresentationField {
  label: string;
  value: string;
  /** Long prose renders full-width rather than in a two-column grid. */
  emphasis?: 'normal' | 'prose';
}

export interface CallPresentationSection {
  id: string;
  title: string;
  fields: CallPresentationField[];
  /** Free-text result (a bare string output) rather than a field set. */
  text?: string;
}

export interface CallGrading {
  leadGrade?: string;
  leadScore?: number;
  assistantScore?: number;
  sentiment?: string;
  category?: string;
  /** Why this lead earned its grade. */
  explanation?: string;
}

export interface CallPresentation {
  callerName: string;
  callerKnown: boolean;
  phone?: string;
  email?: string;
  company?: string;
  summary?: string;
  grading: CallGrading;
  /** Identity + intent, surfaced at the top and on the list card. */
  highlights: CallPresentationField[];
  /** Everything the analysis produced, grouped by structured output. */
  analysisSections: CallPresentationSection[];
  /** Cost and provider internals. Empty unless includeAdmin. */
  adminSections: CallPresentationSection[];
}

export interface CallPresentationInput {
  callerName?: string | null;
  /** The business the caller represents, when the record carries one. */
  companyName?: string | null;
  customerNumber?: string | null;
  phoneNumber?: string | null;
  summary?: string | null;
  leadGrade?: string | null;
  leadScore?: number | null;
  assistantScore?: number | null;
  sentiment?: string | null;
  callCategory?: string | null;
  gradeExplanation?: string | null;
  structuredOutputs?: StructuredOutputRecord[];
  admin?: {
    totalCost?: number | null;
    providerCallId?: string | null;
    providerAssistantId?: string | null;
    endedReason?: string | null;
  };
}

/**
 * Outputs whose content is already promoted into the header (summary, sentiment,
 * scores) are not repeated as their own detail section — the reviewer sees each
 * fact once, in the most prominent place it belongs.
 */
const PROMOTED_OUTPUT_PATTERNS = [/summary/, /sentiment/, /quality_score/];

/** Contact fields are shown as identity, not as a generic analysis section. */
const CONTACT_OUTPUT_PATTERN = /contact|caller/;

export function buildCallPresentation(
  input: CallPresentationInput,
  options: { includeAdmin?: boolean } = {},
): CallPresentation {
  const outputs = input.structuredOutputs ?? [];
  const identity = deriveCallerIdentity(outputs);

  const callerName = firstNonEmpty(input.callerName, identity.fullName);
  const phone = firstNonEmpty(input.customerNumber, identity.phone, input.phoneNumber);

  const grading: CallGrading = {
    leadGrade: orUndefined(input.leadGrade),
    leadScore: numberOrUndefined(input.leadScore),
    assistantScore: numberOrUndefined(input.assistantScore),
    sentiment: orUndefined(input.sentiment),
    category: orUndefined(input.callCategory),
    explanation: orUndefined(input.gradeExplanation),
  };

  return {
    callerName: callerName ?? 'Unknown caller',
    callerKnown: Boolean(callerName),
    phone,
    email: identity.email,
    company: firstNonEmpty(input.companyName, identity.company),
    summary: firstNonEmpty(input.summary),
    grading,
    highlights: buildHighlights(outputs),
    analysisSections: buildAnalysisSections(outputs),
    adminSections: options.includeAdmin ? buildAdminSections(input) : [],
  };
}

/**
 * The handful of facts a reviewer needs before deciding whether to open a call:
 * what they wanted, how urgent, what happened. Drawn from whatever the analysis
 * reported rather than a fixed schema, so a new plan surfaces without a code
 * change.
 */
function buildHighlights(outputs: StructuredOutputRecord[]): CallPresentationField[] {
  const sources = structuredOutputSources(outputs);
  const wanted: Array<[string, string[]]> = [
    ['Service requested', ['service_requested', 'service_needed', 'reason_for_call', 'inquiry_type']],
    ['Urgency', ['urgency_level', 'urgency', 'priority']],
    ['Outcome', ['call_outcome', 'outcome', 'disposition']],
    ['Appointment', ['appointment_date_or_time_requested', 'appointment_requested', 'preferred_time']],
    ['Location', ['location_address', 'caller_location', 'service_address', 'location']],
    ['Budget', ['budget', 'budget_range']],
  ];

  const fields: CallPresentationField[] = [];
  for (const [label, keys] of wanted) {
    const value = pickDisplay(sources, keys);
    if (value) fields.push({ label, value });
  }
  return fields;
}

function buildAnalysisSections(outputs: StructuredOutputRecord[]): CallPresentationSection[] {
  const sections: CallPresentationSection[] = [];

  for (const output of outputs) {
    const slug = slugify(output.name);
    if (PROMOTED_OUTPUT_PATTERNS.some((pattern) => pattern.test(slug))) continue;

    // A scalar result has no fields of its own; show it as text.
    if (output.result === null || typeof output.result !== 'object') {
      sections.push({
        id: output.id,
        title: output.name,
        fields: [],
        text: formatScalar(output.result),
      });
      continue;
    }

    const fields = Object.entries(output.result as Record<string, unknown>)
      .map(([key, value]) => ({
        label: humanizeKey(key),
        value: formatScalar(value),
        emphasis: isProse(value) ? ('prose' as const) : ('normal' as const),
      }))
      .filter((field) => field.value.length > 0);

    if (fields.length > 0) {
      sections.push({ id: output.id, title: output.name, fields });
    }
  }

  // Contact details first — identity is what a reviewer orients on.
  return sections.sort((a, b) => {
    const aContact = CONTACT_OUTPUT_PATTERN.test(slugify(a.title)) ? 0 : 1;
    const bContact = CONTACT_OUTPUT_PATTERN.test(slugify(b.title)) ? 0 : 1;
    return aContact - bContact || a.title.localeCompare(b.title);
  });
}

function buildAdminSections(input: CallPresentationInput): CallPresentationSection[] {
  const admin = input.admin;
  if (!admin) return [];

  const fields: CallPresentationField[] = [];
  if (admin.totalCost !== null && admin.totalCost !== undefined) {
    fields.push({ label: 'Call cost', value: `$${Number(admin.totalCost).toFixed(4)}` });
  }
  if (admin.endedReason) fields.push({ label: 'Ended reason', value: admin.endedReason });
  if (admin.providerCallId) fields.push({ label: 'Provider call ID', value: admin.providerCallId });
  if (admin.providerAssistantId) {
    fields.push({ label: 'Provider assistant ID', value: admin.providerAssistantId });
  }

  return fields.length > 0
    ? [{ id: 'admin', title: 'Internal', fields }]
    : [];
}

/** Human-facing grade label. Letters alone read as arbitrary to a client. */
export function leadGradeLabel(grade: string | undefined): string | undefined {
  if (!grade) return undefined;
  return {
    a: 'Hot lead',
    b: 'Warm lead',
    c: 'Cool lead',
    d: 'Cold lead',
    unqualified: 'Not qualified',
  }[grade.toLowerCase()] ?? grade;
}

export function callCategoryLabel(category: string | undefined): string | undefined {
  if (!category) return undefined;
  return {
    new_project_opportunity: 'New Project / Opportunity',
    existing_project_service_issue: 'Existing Project / Service Issue',
    warranty_callback: 'Warranty / Callback',
    billing: 'Billing',
    scheduling: 'Scheduling',
    vendor: 'Vendor',
    employment: 'Employment',
    solicitor: 'Solicitor',
    spam_robocall: 'Spam / Robocall',
    wrong_number: 'Wrong Number',
    other: 'Other',
  }[category] ?? category.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

/**
 * Values that name nothing. "Other", "Unknown" and their kin are the analysis
 * saying it had no opinion, and a badge that says so costs a reader the same
 * attention as one that says something — so they earn no badge at all. The full
 * stored value stays visible inside the opened record.
 */
const UNINFORMATIVE_VALUES = new Set(['other', 'unknown', 'unclassified', 'none', 'n/a', 'na']);

export function isInformativeValue(value: string | undefined | null): boolean {
  const normalized = value?.trim().toLowerCase();
  return Boolean(normalized) && !UNINFORMATIVE_VALUES.has(normalized!);
}

/**
 * The single call-type badge for a list card or record header. Prefers the
 * portal's configured label over the built-in one, and yields nothing when the
 * category is uninformative.
 */
export function callTypeBadgeLabel(
  category: string | undefined | null,
  configuredLabel?: string | null,
): string | undefined {
  if (!isInformativeValue(category)) return undefined;
  const label = configuredLabel?.trim() || callCategoryLabel(category ?? undefined);
  return isInformativeValue(label) ? label : undefined;
}

/**
 * Sentiment is worth a badge only when it is not the expected outcome. A
 * "Neutral" pill on every call trains the eye to skip the row where a negative
 * one actually appears.
 */
export function isNotableSentiment(sentiment: string | undefined | null): boolean {
  const normalized = sentiment?.trim().toLowerCase();
  if (!normalized) return false;
  return !['neutral', 'normal', 'mixed', 'unknown'].includes(normalized);
}

export function callerRelationshipLabel(relationship: string | undefined): string | undefined {
  if (!relationship) return undefined;
  return {
    new_contact: 'New Contact',
    existing_contact: 'Existing Contact',
    unknown: 'Unknown',
  }[relationship] ?? relationship;
}

export function leadEligibilityLabel(eligibility: string | undefined): string | undefined {
  if (!eligibility) return undefined;
  return {
    eligible: 'Eligible',
    not_eligible: 'Not Eligible',
    needs_review: 'Needs Review',
  }[eligibility] ?? eligibility;
}

function pickDisplay(sources: Array<Record<string, unknown>>, keys: string[]): string | undefined {
  for (const source of sources) {
    for (const key of keys) {
      if (!(key in source)) continue;
      const formatted = formatScalar(source[key]);
      if (formatted) return formatted;
    }
  }
  return undefined;
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(formatScalar).filter(Boolean).join(', ');
  return '';
}

function isProse(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 80;
}

function humanizeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function orUndefined(value: string | null | undefined): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function numberOrUndefined(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
